import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import PreviewQRCard from "@/components/share/PreviewQRCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, QrCode, Search, Printer } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function PreviewQRPanel({ initialShopId = null }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [revokeShop, setRevokeShop] = useState(null);
  const [paths, setPaths] = useState([]);
  const [sheetPath, setSheetPath] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/rider-preview/qr-codes"); setRows(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); api.get("/bike-paths", { params: { lite: 1 } }).then((r) => setPaths(r.data)).catch(() => {}); }, []);

  const patch = (updated) => setRows((rs) => rs.map((r) => (r.shopId === updated.shopId ? updated : r)));

  const generate = async (shopId) => {
    setBusyId(shopId);
    try { const { data } = await api.post("/admin/rider-preview/qr-code/regenerate", { shopId }); patch(data); toast.success("QR code ready"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const doRevoke = async () => {
    const shopId = revokeShop; setRevokeShop(null); setBusyId(shopId);
    try { const { data } = await api.post("/admin/rider-preview/revoke", { shopId }); patch(data); toast.success("Preview link revoked"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  if (!rows) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const filtered = rows
    .filter((r) => !q || (r.shopName || "").toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (initialShopId ? (a.shopId === initialShopId ? -1 : b.shopId === initialShopId ? 1 : 0) : 0));

  return (
    <div className="space-y-4" data-testid="preview-qr-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <QrCode className="w-5 h-5 text-primary" />
          <div>
            <h2 className="font-heading text-lg font-bold">Rider Preview QR Codes</h2>
            <p className="text-sm text-muted-foreground">Generate scannable QR codes shops can print, share, or use during onboarding.</p>
          </div>
        </div>
        <div className="flex items-end gap-2">
          <Select value={sheetPath} onValueChange={setSheetPath}>
            <SelectTrigger className="w-52" data-testid="qr-sheet-path-select"><SelectValue placeholder="Bike path for print sheet" /></SelectTrigger>
            <SelectContent>{paths.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" disabled={!sheetPath} onClick={() => window.open(`/admin/print-qr/${sheetPath}`, "_blank")} data-testid="qr-print-sheet-btn"><Printer className="w-4 h-4 mr-1" />Print QR Sheet</Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search shops…" className="pl-9" data-testid="qr-search" />
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground" data-testid="qr-empty">
          No shops with an assigned zone yet. Assign a zone to a shop first.
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((r) => (
            <div key={r.shopId} data-testid={`qr-row-${r.shopId}`} className={initialShopId === r.shopId ? "ring-2 ring-primary rounded-2xl" : ""}>
              <PreviewQRCard qr={r} busy={busyId === r.shopId} showRevoke onRegenerate={() => generate(r.shopId)} onRevoke={() => setRevokeShop(r.shopId)} />
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!revokeShop} onOpenChange={(o) => !o && setRevokeShop(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this preview link?</AlertDialogTitle>
            <AlertDialogDescription>The current QR code will stop working. Anyone scanning it will see a "Preview link revoked" message. You can generate a new link and QR code afterward.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRevoke} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="qr-confirm-revoke">Revoke link</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
